import { useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { SignupValues } from '../context/AuthContext';

type Props = {
  onSubmit(values: SignupValues): Promise<void>;
  disabled?: boolean;
};

export const SignupForm = ({ onSubmit, disabled = false }: Props) => {
  const { t } = useTranslation();
  const formSchema = useMemo(
    () =>
      z
        .object({
          email: z.string().email({ message: t('auth_page.email_error') }),
          password: z.string().min(6, { message: t('auth_page.password_error') }),
          confirmPassword: z.string().min(6, { message: t('auth_page.password_error') }),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: t('auth_page.confirm_password_error'),
          path: ['confirmPassword'],
        }),
    [t]
  );
  const { register, handleSubmit, formState } = useForm<SignupValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '', password: '', confirmPassword: '' },
    mode: 'onChange',
  });
  const isPending = disabled || formState.isSubmitting;
  const fields = [
    { name: 'email', type: 'email', autoComplete: 'email', label: 'auth_page.email_label' },
    {
      name: 'password',
      type: 'password',
      autoComplete: 'new-password',
      label: 'auth_page.password_label',
    },
    {
      name: 'confirmPassword',
      type: 'password',
      autoComplete: 'new-password',
      label: 'auth_page.confirm_password_label',
    },
  ] as const;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="redemption-form" noValidate>
      {fields.map(({ name, type, autoComplete, label }) => {
        const error = formState.touchedFields[name] ? formState.errors[name]?.message : undefined;
        const id = `signup-${name}`;
        return (
          <div key={name}>
            <label htmlFor={id}>{t(label)}</label>
            <input
              id={id}
              type={type}
              autoComplete={autoComplete}
              required
              disabled={isPending}
              aria-invalid={!!error}
              aria-describedby={error ? `${id}-error` : undefined}
              {...register(name)}
            />
            {error && (
              <p id={`${id}-error`} className="redemption-field-error" role="alert">
                {error}
              </p>
            )}
          </div>
        );
      })}
      <button
        type="submit"
        className="redemption-primary"
        disabled={!formState.isValid || isPending}
        aria-busy={isPending}
      >
        {t('auth_page.signup_button')}
        <ArrowRight size={18} aria-hidden="true" />
      </button>
    </form>
  );
};
